/**
 * سياسة الخصوصية.
 *
 * مبنية على ما يجمعه النظام **فعلاً** — لا على قالب عام. كل حقل مذكور هنا
 * موجود في قاعدة البيانات، وكل حقل في قاعدة البيانات مذكور هنا. سياسة تَعِد
 * بأكثر مما يفعله النظام كذبٌ مهذّب، وسياسة تُغفل ما يجمعه النظام إخفاء.
 *
 * ترتيب البنود يتبع المادة (١٢) من نظام حماية البيانات الشخصية: الغرض ·
 * محتوى البيانات · طريقة الجمع والحفظ · المعالجة والإتلاف · الحقوق · كيفية
 * ممارستها. والحقوق الخمسة من المادة (٤).
 *
 * ⚠️ صياغة أولية معدّة داخلياً، **لم تُراجَع من محامٍ سعودي**.
 */

import { color } from '@/design/tokens';
import { LegalPage, LegalSection, L, Notice, P, LEGAL_CONTACT } from './LegalPage';

export default function PrivacyRoute(): JSX.Element {
  return (
    <LegalPage
      stamp="NITAQ / PRIVACY"
      title="سياسة الخصوصية"
      subtitle="ما نجمعه عنك، ولماذا، وأين يُحفظ، وكيف تتحكّم فيه."
      testId="privacy-page"
    >
      <Notice>
        <strong style={{ color: color.text }}>الخلاصة:</strong> لا نجمع عنك إلا رقم جوالك
        واسمك إن أدخلته، وما تحفظه بنفسك في قائمة المراقبة. لا نبيع بياناتك ولا نشاركها
        لأغراض تسويقية، ولا نستخدم أدوات تتبّع إعلاني.
      </Notice>

      <LegalSection number="١" title="الغرض من جمع البيانات">
        <P>نجمع أقلّ ما يلزم لتشغيل الخدمة، وللأغراض التالية فقط:</P>
        <L
          items={[
            'التحقّق من هويتك عند الدخول برقم الجوال.',
            'حفظ ما تختاره أنت: الأحياء في قائمة المراقبة.',
            'حماية الخدمة من الاستخدام الآلي والإساءة.',
            'فهم الأعطال وإصلاحها.',
          ]}
        />
        <P>
          لا نستخدم بياناتك لأي غرض تسويقي، ولا لبناء ملفّ سلوكي عنك، ولا لبيعها أو
          مشاركتها مع معلنين.
        </P>
      </LegalSection>

      <LegalSection number="٢" title="محتوى البيانات التي نجمعها">
        <P>
          <strong style={{ color: color.text }}>ما نحفظه في قاعدة البيانات:</strong>
        </P>
        <L
          items={[
            'رقم الجوال — لإنشاء الحساب والدخول.',
            'الاسم — اختياري، لمخاطبتك داخل المنصّة.',
            'رمز تحقّق مؤقّت — صلاحيته عشر دقائق ثم يفقد أثره.',
            'قائمة المراقبة — الأحياء التي تحفظها بنفسك.',
            'تاريخ إنشاء الحساب وآخر دخول.',
          ]}
        />
        <P>
          <strong style={{ color: color.text }}>ما نحفظه في متصفّحك أنت</strong> (لا يصلنا
          ولا يُرسَل إلى خوادمنا): رمز جلستك بعد الدخول، وآخر مدينة اخترتها في الخريطة.
        </P>
        <P>
          <strong style={{ color: color.text }}>ما لا نجمعه إطلاقاً:</strong>
        </P>
        <L
          items={[
            'رقم الهوية الوطنية أو صورة أي وثيقة.',
            'أي بيانات بنكية أو بطاقات — لا توجد مدفوعات في المنصّة.',
            'موقعك الجغرافي.',
            'دفتر جهات اتصالك أو صورك أو ملفاتك.',
          ]}
        />
        <P>
          ولا نستخدم ملفات ارتباط تتبّع إعلاني، ولا أدوات تحليل سلوك المستخدمين من طرف
          ثالث.
        </P>
      </LegalSection>

      <LegalSection number="٣" title="طريقة الجمع">
        <P>
          نجمع بياناتك <strong style={{ color: color.text }}>منك مباشرةً</strong> حين
          تُدخلها: رقم جوالك عند الدخول، واسمك إن أضفته، والأحياء التي تحفظها. لا نشتري
          بيانات عنك من أي جهة، ولا نستقيها من مصادر خارجية.
        </P>
        <P>
          أمّا بيانات الصفقات والسوق المعروضة في المنصّة فليست بيانات شخصية: هي صفقات
          مسجّلة رسمياً بلا أسماء ولا هويات أطرافها.
        </P>
      </LegalSection>

      <LegalSection number="٤" title="مكان الحفظ ونقل البيانات خارج المملكة">
        <Notice>
          <strong style={{ color: color.text }}>إفصاح مهم:</strong> تُحفظ بيانات الحسابات
          حالياً على خوادم مزوّد بنية تحتية سحابية في{' '}
          <strong style={{ color: color.text }}>فرانكفورت — الاتحاد الأوروبي</strong>، أي
          خارج المملكة العربية السعودية.
        </Notice>
        <P>
          الأساس في ذلك تقديم الخدمة لك بناءً على طلبك، وضمن الحدّ الأدنى من البيانات
          اللازمة لتشغيلها. ونعمل على مراجعة هذا الترتيب بما يتوافق مع نظام حماية البيانات
          الشخصية ولائحته التنفيذية وضوابط نقل البيانات خارج المملكة.
        </P>
        <P>
          إن كان حفظ بياناتك خارج المملكة غير مقبول لك، فلا تُنشئ حساباً — وتصفّح المنصّة
          يعمل كاملاً بلا تسجيل دخول.
        </P>
      </LegalSection>

      <LegalSection number="٥" title="المعالجة والإفصاح للغير">
        <P>
          لا نُفصح عن بياناتك الشخصية لأي طرف ثالث، إلا في حالتين: مزوّد البنية التحتية
          الذي يستضيف قاعدة البيانات نيابةً عنّا، أو طلب نظامي من جهة مختصّة في المملكة.
        </P>
        <P>لا نبيع بياناتك، ولا نؤجّرها، ولا نتبادلها مع شركاء تسويقيين.</P>
      </LegalSection>

      <LegalSection number="٦" title="مدة الحفظ والإتلاف">
        <L
          items={[
            'رمز التحقّق: عشر دقائق، ثم يفقد صلاحيته.',
            'جلسة الدخول: ثلاثون يوماً، ثم يلزم دخول جديد.',
            'بيانات الحساب وقائمة المراقبة: ما دام حسابك قائماً.',
            'عند طلب حذف حسابك: تُتلَف بياناتك الشخصية ولا تُحتفظ نسخة تعريفية منها.',
          ]}
        />
      </LegalSection>

      <LegalSection number="٧" title="حقوقك">
        <P>
          يمنحك نظام حماية البيانات الشخصية في المملكة الحقوق التالية، ونلتزم بها:
        </P>
        <L
          items={[
            <>
              <strong style={{ color: color.text }}>حق العلم</strong> — أن تعرف المسوّغ
              النظامي لجمع بياناتك والغرض منه. وهذه الصفحة أداة هذا الحق.
            </>,
            <>
              <strong style={{ color: color.text }}>حق الوصول</strong> — أن تطلب نسخة من
              بياناتك المتوافرة لدينا.
            </>,
            <>
              <strong style={{ color: color.text }}>حق الحصول عليها بصيغة مقروءة</strong> —
              أن تستلمها بصيغة واضحة قابلة للقراءة.
            </>,
            <>
              <strong style={{ color: color.text }}>حق التصحيح والتحديث</strong> — أن تطلب
              تصحيح بياناتك أو إتمامها أو تحديثها.
            </>,
            <>
              <strong style={{ color: color.text }}>حق الإتلاف</strong> — أن تطلب حذف
              بياناتك متى انتهت الحاجة إليها.
            </>,
          ]}
        />
      </LegalSection>

      <LegalSection number="٨" title="كيف تمارس حقوقك">
        <P>
          أرسل طلبك من بريدك أو برقم جوالك المسجّل إلى{' '}
          <a href={`mailto:${LEGAL_CONTACT}`} style={{ color: color.teal }}>
            {LEGAL_CONTACT}
          </a>{' '}
          موضّحاً ما تطلبه. نردّ خلال مدة معقولة، وقد نتحقّق من هويتك قبل التنفيذ حمايةً
          لحسابك من طلب ليس منك.
        </P>
        <P>
          وإن لم يصلك ردّ مُرضٍ، فلك التقدّم بشكوى إلى الجهة المختصّة بالإشراف على تطبيق
          نظام حماية البيانات الشخصية في المملكة.
        </P>
      </LegalSection>

      <LegalSection number="٩" title="أمن البيانات">
        <P>
          الوصول إلى قاعدة البيانات مقصور على الخادم وحده بمفتاح لا يصل المتصفّح، وسياسات
          أمن الصفوف مفعّلة على كل الجداول بلا استثناء. ومع ذلك، لا يوجد نظام آمن بالمطلق،
          ولا نَعِد بما لا يستطيع أحد ضمانه.
        </P>
      </LegalSection>

      <LegalSection number="١٠" title="الأطفال">
        <P>
          الخدمة موجّهة لمن أتمّ الثامنة عشرة. ولا نجمع عن قصد بيانات ممّن هم دون ذلك، وإن
          تبيّن خلاف ذلك تُحذف البيانات.
        </P>
      </LegalSection>

      <LegalSection number="١١" title="تحديث السياسة">
        <P>
          قد تُحدَّث هذه السياسة. النسخة السارية هي المنشورة هنا بتاريخها الظاهر أعلاه،
          ونُعلن التغييرات الجوهرية داخل المنصّة قبل سريانها.
        </P>
      </LegalSection>
    </LegalPage>
  );
}
